/**
 * Enumeration for log levels.
 */
export enum LogLevel {
  DEBUG,
  INFO,
  WARN,
  ERROR
}

/**
 * Logger configuration options.
 */
export interface LoggerOptions {
  enabled?: boolean
  level?: LogLevel
}

/**
 * Utility logger that displays colored logs.
 */
export class Logger {
  private static options: LoggerOptions = {
    enabled: true,
    level: LogLevel.DEBUG
  }

  private static resetColor = '\x1b[0m'
  private static colors = {
    [LogLevel.DEBUG]: '\x1b[34m', // Blue
    [LogLevel.INFO]: '\x1b[32m', // Green
    [LogLevel.WARN]: '\x1b[33m', // Yellow
    [LogLevel.ERROR]: '\x1b[31m' // Red
  }

  /**
   * Updates the logger options.
   * @param options - New configuration options.
   */
  static setOptions(options: LoggerOptions): void {
    this.options = { ...this.options, ...options }
  }

  /**
   * Checks if the message with the provided level should be logged.
   * @param level - The log level.
   * @returns True if logging is enabled for the given level.
   */
  private static shouldLog(level: LogLevel): boolean {
    return Boolean(this.options.enabled) && level >= (this.options.level ?? LogLevel.DEBUG)
  }

  /**
   * Logs a debug message.
   * @param message - The message to be logged.
   * @param optionalParams - Additional parameters.
   */
  static debug(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const color = this.colors[LogLevel.DEBUG]
      console.debug(`${color}[DEBUG]${this.resetColor} ${message}`, ...optionalParams)
    }
  }

  /**
   * Logs an informational message.
   * @param message - The message to be logged.
   * @param optionalParams - Additional parameters.
   */
  static info(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const color = this.colors[LogLevel.INFO]
      console.info(`${color}[INFO]${this.resetColor} ${message}`, ...optionalParams)
    }
  }

  /**
   * Logs a warning message.
   * @param message - The message to be logged.
   * @param optionalParams - Additional parameters.
   */
  static warn(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const color = this.colors[LogLevel.WARN]
      console.warn(`${color}[WARN]${this.resetColor} ${message}`, ...optionalParams)
    }
  }

  /**
   * Logs an error message.
   * @param message - The message to be logged.
   * @param optionalParams - Additional parameters.
   */
  static error(message: string, ...optionalParams: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const color = this.colors[LogLevel.ERROR]
      console.error(`${color}[ERROR]${this.resetColor} ${message}`, ...optionalParams)
    }
  }
}
